import { useCallback, useEffect, useId, useState } from 'react';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { trackStorageEvent } from '../../services/storageAnalytics';
import { storageDateTimeUtc } from '../../utils/storageDateTime';
import {
  isoToLocalInput, LOCAL_TIME_GAP_MESSAGE, LOCAL_TIME_HINT, LOCAL_TIME_INVALID_MESSAGE, resolveLocalInput, utcOffsetLabel,
} from './localDateTime';

type ReportProblem = (fieldId: string, problem: string | undefined) => void;

/** Field problems of one dialog: any refused date blocks its Generate / Save (review D RD4). */
export function useDateFieldProblems() {
  const [problems, setProblems] = useState<Record<string, string>>({});
  const report = useCallback<ReportProblem>((fieldId, problem) => setProblems((current) => {
    if (current[fieldId] === problem) return current;
    const next = { ...current };
    if (problem) next[fieldId] = problem; else delete next[fieldId];
    return next;
  }), []);
  // F12: `problem` is the reason a dialog's submit button shows while a date field blocks it.
  return { report, blocked: Object.keys(problems).length > 0, problem: Object.values(problems)[0] };
}

/**
 * A local-time date field under the one DST rule of `localDateTime.ts`: a skipped time stays in the input with a visible
 * refusal (and blocks the dialog) instead of being moved; a repeated time keeps the field's current offset, shows it,
 * and offers the other occurrence.
 */
export function LocalDateTimeField(props: {
  label: string;
  /** Which dialog the field sits in, for the occurrence-switch event only. */
  dialog: 'sas' | 'access-policies';
  value: string | undefined;
  disabled?: boolean;
  showNotSet?: boolean;
  /** Where a locked value comes from ("From policy x", A1). */
  note?: string;
  onChange: (iso: string | undefined) => void;
  onProblem: ReportProblem;
}) {
  const { label, value, onProblem } = props;
  const fieldId = useId();
  const [text, setText] = useState(() => isoToLocalInput(value));
  const [problem, setProblem] = useState<string>();
  useEffect(() => { setText(isoToLocalInput(value)); setProblem(undefined); onProblem(fieldId, undefined); }, [value, fieldId, onProblem]);
  useEffect(() => () => onProblem(fieldId, undefined), [fieldId, onProblem]);
  const edit = (next: string) => {
    setText(next);
    const resolved = resolveLocalInput(next, value);
    const refusal = resolved.kind === 'gap' ? LOCAL_TIME_GAP_MESSAGE : resolved.kind === 'invalid' ? LOCAL_TIME_INVALID_MESSAGE : undefined;
    setProblem(refusal);
    onProblem(fieldId, refusal);
    if (resolved.kind === 'ok' && resolved.iso !== value) props.onChange(resolved.iso);
    if (resolved.kind === 'empty' && value) props.onChange(undefined);
  };
  const current = value && !problem ? resolveLocalInput(isoToLocalInput(value), value) : undefined;
  const other = current?.kind === 'ok' ? current.other : undefined;
  const occurrence = other && Date.parse(other) > Date.parse(value!) ? 'later' : 'earlier';
  const switchOccurrence = () => {
    trackStorageEvent('feature:storage_date_occurrence', { dialog: props.dialog, occurrence });
    props.onChange(other);
  };
  return <div className="text-xs">
    <label>{label} <span className="text-muted-foreground">({LOCAL_TIME_HINT})</span>{props.showNotSet && !value && !problem && <span className="ml-1 text-muted-foreground">not set</span>}
      <Input type="datetime-local" aria-label={label} disabled={props.disabled} value={text} onChange={(event) => edit(event.target.value)} />
    </label>
    <div className="mt-1 flex flex-wrap items-center gap-2">
      {/* C1 (N11): the offset only where it tells something (a local zone off UTC, or a repeated DST hour); the UTC
          echo to the second — "UTC+00:00" beside "= … .646 UTC" repeated itself in a UTC zone. */}
      {value && !problem && (other || utcOffsetLabel(value) !== 'UTC+00:00') && <span aria-label={`${label} UTC offset`} className="text-muted-foreground">{utcOffsetLabel(value)}</span>}
      {value && !problem && <span aria-label={`${label} in UTC`} className="text-muted-foreground">= {storageDateTimeUtc(value)?.replace(/\.\d{3} UTC$/u, ' UTC')}</span>}
      {props.note && <span className="font-medium text-muted-foreground" data-testid="storage-date-note">{props.note}</span>}
      {other && !props.disabled && <Button type="button" size="xs" variant="ghost" onClick={switchOccurrence}>
        {`Use the ${occurrence} ${text.slice(11, 16)} (${utcOffsetLabel(other)})`}
      </Button>}
      {problem && <span role="alert" className="text-destructive">{problem}</span>}
    </div>
  </div>;
}
